'use client'

import { motion } from 'framer-motion'
import type { ReactNode } from 'react'

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

export const CONSULTATION_SECTION_MAX_WIDTH = 'max-w-5xl'

interface ConsultationCTASectionProps {
  eyebrow: string
  title: string
  description: string
  secondaryText?: string
  actions: ReactNode
  leadingIcon?: ReactNode
  accent?: 'emerald' | 'red'
  className?: string
}

export function ConsultationCTASection({
  eyebrow,
  title,
  description,
  secondaryText,
  actions,
  leadingIcon,
  accent = 'emerald',
  className,
}: ConsultationCTASectionProps) {
  const accentBlur =
    accent === 'red'
      ? 'bg-brand-red/20 -bottom-16 -right-16'
      : 'bg-brand-green/20 -bottom-16 -right-16'

  return (
    <motion.section
      whileHover={{ y: -4 }}
      className={cn(
        CONSULTATION_SECTION_MAX_WIDTH,
        // Same navy panel as the home page "Ready to start Japanese?" section
        'mx-auto w-full bg-brand-navy text-white rounded-[2rem] px-8 py-12 md:px-14 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden shadow-[0_30px_60px_-30px_rgba(20,33,61,0.55)]',
        className
      )}
    >
      <div className="absolute inset-0 rounded-[2rem] pointer-events-none border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]" />
      <div className="absolute top-0 left-12 right-12 h-px bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent" />
      <div className={cn('absolute w-56 h-56 rounded-full blur-3xl', accentBlur)} />

      <div
        className={cn(
          'z-10 text-center md:text-left',
          leadingIcon
            ? 'flex items-start gap-5 flex-col md:flex-row md:items-center w-full md:w-auto'
            : 'space-y-3'
        )}
      >
        {leadingIcon && (
          <div className="w-14 h-14 shrink-0 rounded-2xl bg-white/10 flex items-center justify-center mx-auto md:mx-0">
            {leadingIcon}
          </div>
        )}
        <div className={leadingIcon ? 'space-y-2' : 'space-y-3'}>
          <p
            className={cn(
              'section-eyebrow !text-brand-gold',
              leadingIcon && 'justify-center md:justify-start w-full'
            )}
          >
            {eyebrow}
          </p>
          <h2 className="type-section-title !text-white !text-2xl md:!text-3xl">{title}</h2>
          <p className="text-[15px] md:text-lg text-white/80 max-w-md leading-[1.72] text-pretty">{description}</p>
          {secondaryText && (
            <p className="text-sm text-white/60 max-w-md leading-relaxed">{secondaryText}</p>
          )}
        </div>
      </div>

      <div className="z-10 flex flex-col sm:flex-row items-center gap-4 shrink-0">{actions}</div>
    </motion.section>
  )
}
